import type { WidgetComponent } from '@zendesk/forethought-elements';
import type {
  AgentAssistView,
  AgentUserView,
  MetaView,
  PreviewIntentFilter,
  RelatedArticle,
  StreamChunk,
} from 'src/services/assist-preview/types';

export interface PreviewLog {
  log_type: string;
  message?: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
  title: string;
}

export interface TicketEvent {
  timestamp: string;
  type: 'customer' | 'agent';
}

export interface AssistPreviewSliceState {
  // Agent-Assist View state
  agentAssistView: {
    isStreaming: boolean;
    messages: AgentAssistView['messages'];
    relatedDocuments: RelatedArticle[];
    streamingContent: string;
    suggestions: string[];
    summary: string;
    widgetComponents: WidgetComponent[];
  };

  // Agent-User View state
  agentUserView: {
    messages: AgentUserView['messages'];
  };

  // Session configuration
  config: {
    contextVariables: Record<string, boolean | number | string>;
    intentFilters: Record<string, PreviewIntentFilter>;
    locale: string;
  };

  // Error state
  error: {
    code?: string;
    message: string;
  } | null;

  // Loading states
  isConverseLoading: boolean;
  isReplyLoading: boolean;
  isStarting: boolean;

  // Meta view state
  metaView: MetaView;

  // Preview logs from Pusher events
  previewLogs: PreviewLog[];

  // Reset counter (used as React key to force remount on restart)
  resetCounter: number;

  // Session IDs
  session: {
    conversationId: string | null;
    previewSessionId: string | null;
  };

  // Streaming chunks buffer
  streamChunks: StreamChunk[];

  // Ticket chat events for dividers in Assist Widget
  ticketEvents: TicketEvent[];
}

export const initialAssistPreviewState: AssistPreviewSliceState = {
  agentAssistView: {
    isStreaming: false,
    messages: [],
    relatedDocuments: [],
    streamingContent: '',
    suggestions: [],
    summary: '',
    widgetComponents: [],
  },
  agentUserView: {
    messages: [],
  },
  config: {
    contextVariables: {},
    intentFilters: {},
    locale: 'en',
  },
  error: null,
  isConverseLoading: false,
  isReplyLoading: false,
  isStarting: false,
  metaView: {
    context_variables: {},
    current_intent_id: null,
    current_intent_title: null,
    language: 'en',
  },
  previewLogs: [],
  resetCounter: 0,
  session: {
    conversationId: null,
    previewSessionId: null,
  },
  streamChunks: [],
  ticketEvents: [],
};
